import { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatFavouriteIcon as ChatFavouriteIcon, Cancel01Icon, CheckmarkCircle01Icon } from "@hugeicons/core-free-icons";
import {
  resolveSurveyStatus,
  useCheckHasSubmittedSurveyQuery,
  useFetchPostOrderSurveyQuery,
} from "@/api/surveysApi";
import { useResponsive } from "@/hooks";
import { Order } from "@/types";
import SurveyResponseForm from "@/pages/surveys/components/SurveyResponseForm";

interface PostOrderSurveyCardProps {
  organizationId?: string;
  surveyId?: string | null;
  order?: Order;
}

const PostOrderSurveyCard = ({
  organizationId,
  surveyId,
  order,
}: PostOrderSurveyCardProps) => {
  const { isMobile } = useResponsive();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const hasAutoOpenedRef = useRef(false);

  const surveyQuery = useFetchPostOrderSurveyQuery(
    {
      organizationId: organizationId || "",
      surveyId,
    },
    { skip: !organizationId }
  );

  const survey = surveyQuery.data;
  const surveyStatus = resolveSurveyStatus(survey);

  // Usa .count().get() (getCountFromServer) para validar si esta orden ya fue calificada
  const hasSubmittedQuery = useCheckHasSubmittedSurveyQuery(
    {
      organizationId: organizationId || "",
      surveyId: survey?.id || "",
      orderId: order?.id,
    },
    { skip: !organizationId || !survey?.id || !order?.id }
  );

  // const alreadyAnswered = Boolean(hasSubmittedQuery.data) || justSubmitted;
  const alreadyAnswered = false;

  // Abrir automáticamente el drawer una vez cuando carga la vista de estatus entregado y aún no ha respondido
  useEffect(() => {
    if (
      !hasAutoOpenedRef.current &&
      survey &&
      surveyStatus === "active" &&
      survey.isPublished &&
      (survey.questions?.length ?? 0) > 0 &&
      !hasSubmittedQuery.isLoading &&
      !alreadyAnswered
    ) {
      hasAutoOpenedRef.current = true;
      setIsDrawerOpen(true);
    }
  }, [survey, surveyStatus, hasSubmittedQuery.isLoading, alreadyAnswered]);

  if (!organizationId || surveyQuery.isLoading || hasSubmittedQuery.isLoading) {
    return null;
  }

  if (!survey || surveyStatus !== "active" || !survey.isPublished || !survey.questions?.length) {
    return null;
  }

  const clientObj = typeof order?.client === "object" ? (order?.client as any) : null;
  const clientId =
    clientObj?.uid ||
    clientObj?.id ||
    (typeof order?.client === "string" ? order.client : null);

  return (
    <>
      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
        }}
      >
        {alreadyAnswered ? (
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
            <HugeiconsIcon icon={CheckmarkCircle01Icon} size={22} color="#16a34a" />
            <Typography variant="body1" fontWeight={600} sx={{ color: "#15803d" }}>
              ¡Gracias por calificar tu pedido!
            </Typography>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                variant="rounded"
                sx={{
                  padding: 1,
                  bgcolor: "rgba(1, 95, 238, 0.1)",
                  color: "#015FEE",
                }}
              >
                <HugeiconsIcon icon={ChatFavouriteIcon} size={32} strokeWidth={1.5} />
              </Avatar>
              <Stack spacing={0.25}>
                <Typography variant="subtitle1">
                  {survey.title || "¿Cómo estuvo tu pedido?"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {survey.description || "Regálanos 30 segundos para calificar tu experiencia"}
                </Typography>
              </Stack>
            </Stack>

            <Button
              variant="outlined"
              size="large"
              onClick={() => setIsDrawerOpen(true)}
            >
              Llenar encuesta
            </Button>
          </Stack>
        )}
      </Paper>

      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={isDrawerOpen && !alreadyAnswered}
        onClose={() => setIsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : 600,
            maxHeight: isMobile ? "88vh" : "100vh",
            p: 0,
            bgcolor: "#F4F7FE",
            borderTopLeftRadius: isMobile ? 24 : 0,
            borderTopRightRadius: isMobile ? 24 : 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          p={2.5}
          sx={{
            bgcolor: "#fff",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                padding: 1,
                bgcolor: "rgba(1, 95, 238, 0.1)",
                color: "#015FEE",
              }}
            >
              <HugeiconsIcon icon={ChatFavouriteIcon} size={32} strokeWidth={1.5} />
            </Avatar>
            <Box>
              <Typography variant="h4">
                {survey.title || "¿Cómo estuvo tu pedido?"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {order?.number
                  ? `Pedido #${order.number} • Califica tu experiencia`
                  : "Califica tu experiencia"}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={() => setIsDrawerOpen(false)} size="small">
            <HugeiconsIcon icon={Cancel01Icon} size={24} />
          </IconButton>
        </Stack>

        {/* Form Body */}
        <Box flex={1} overflow="auto" p={2.5}>
          <SurveyResponseForm
            survey={survey}
            organizationId={organizationId}
            orderId={order?.id ?? null}
            orderNumber={order?.number ?? null}
            branchId={order?.branchId ?? (order?.branch as any)?.id ?? null}
            tableId={order?.tableId ?? null}
            flow={order?.flow ?? null}
            clientId={clientId}
            clientName={clientObj?.name ?? null}
            clientPhone={clientObj?.phone ?? clientObj?.phoneNumber ?? null}
            onSubmitted={() => {
              const hasRatingQuestion = (survey.questions ?? []).some(
                (q) => q.type === "rating"
              );
              if (!hasRatingQuestion) {
                setIsDrawerOpen(false);
              }
            }}
            onCancel={() => setIsDrawerOpen(false)}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default PostOrderSurveyCard;
